import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css'
import {FC, useState} from 'react'
import {useParams, useNavigate} from 'react-router'
import {useQueryClient, useQuery, useMutation} from 'react-query'
import {toast} from 'react-toastify'
import {FieldArray, FormikProvider, useFormik} from 'formik'
import ModalRichEditor from '../../../../components/sections/ModalRichEditor'
import {
  getSelfCareByIdWithGroupedPerWeek,
  getSelfCareByCycle,
  getSelfCareCategories,
  bulkCreateUpdateActivities,
  deleteActivities,
} from '../../../../modules/services/self-care.service'
import {uploadFiles} from '../../../../modules/services/upload.service'
import {InputDefault} from '../../../../components/ui'
import {initialValueMock} from '../../../../mocks'

const SelfCareDetailWrapper: FC = () => {
  const navigate = useNavigate()
  let bodyCategoryId: number, mindCategoryId: number
  const [selectedDescription, setSelectedDescription] = useState({
    name: '',
    value: '',
  })
  const {id} = useParams<{id: string}>()
  const queryClient = useQueryClient()
  const {data: selfCare} = useQuery(['getSelfCareByIdWithGroupedPerWeek', id], () =>
    getSelfCareByIdWithGroupedPerWeek(id)
  )
  const {data: selfCareCategories} = useQuery('getSelfCareCategories', () =>
    getSelfCareCategories()
  )
  const {data: prevCycle} = useQuery(['getSelfCareByCycle', selfCare?.attributes.cycle + 1], () =>
    getSelfCareByCycle(selfCare?.attributes.cycle - 1)
  )
  const {data: nextCycle} = useQuery(['getSelfCareByCycle', selfCare?.attributes.cycle - 1], () =>
    getSelfCareByCycle(selfCare?.attributes.cycle + 1)
  )
  bodyCategoryId = (selfCareCategories as Array<any>)?.find(
    (category) => (category.attributes.name as string).toLowerCase() === 'body'
  )?.id
  mindCategoryId = (selfCareCategories as Array<any>)?.find(
    (category) => (category.attributes.name as string).toLowerCase() === 'mind'
  )?.id
  const uploadMutation = useMutation(uploadFiles)

  const mutation = useMutation(bulkCreateUpdateActivities, {
    onSuccess: () => {
      queryClient.invalidateQueries('getSelfCareByIdWithGroupedPerWeek')
      toast.success('Update Success')
    },
    onError(error: any, variables, context) {
      console.log('Error', error.response?.data)
      toast.error(error.response?.data?.error?.message)
    },
  })

  const deleteMutation = useMutation(deleteActivities, {
    onSuccess: () => {
      queryClient.invalidateQueries('getSelfCareByIdWithGroupedPerWeek')
      toast.success('Delete Success')
    },
    onError(error: any, variables, context) {
      console.log('Error', error.response?.data)
      toast.error(error.response?.data?.error?.message)
    },
  })

  const formik = useFormik({
    initialValues:
      selfCare?.attributes?.activities?.data?.length > 0
        ? {
            weeks: (selfCare.attributes.activities.data as Array<any>).map((item) => {
              const categoryBody = (item.attributes.data as Array<any>)?.find(
                (activity) => (activity.category.name as string).toLowerCase() === 'body'
              )
              const categoryMind = (item.attributes.data as Array<any>)?.find(
                (activity) => (activity.category.name as string).toLowerCase() === 'mind'
              )
              return {
                week: item.attributes.week as number,
                body: categoryBody
                  ? {
                      id: categoryBody.id as number,
                      title: categoryBody.title as string,
                      url: categoryBody.url as string,
                      category: bodyCategoryId,
                      imageUrl: categoryBody?.image?.url as string,
                      description: categoryBody.description as string,
                      file: {},
                    }
                  : {
                      title: '',
                      url: '',
                      category: bodyCategoryId,
                      description: '',
                      file: {},
                    },
                mind: categoryMind
                  ? {
                      id: categoryMind.id as number,
                      title: categoryMind.title as string,
                      url: categoryMind.url as string,
                      category: mindCategoryId,
                      imageUrl: categoryMind?.image?.url as string,
                      description: categoryMind.description as string,
                      file: {},
                    }
                  : {
                      title: '',
                      url: '',
                      category: mindCategoryId,
                      description: '',
                      file: {},
                    },
              }
            }),
          }
        : initialValueMock.selfCare,
    onSubmit: async (values) => {
      const dataWithFile = values.weeks.flatMap((item) => {
        return [
          {
            activityId: item.body.id || undefined,
            selfCareId: id,
            title: item.body.title,
            url: item.body.url,
            category: bodyCategoryId,
            description: item.body.description,
            file: item.body.file,
            week: item.week,
          },
          {
            activityId: item.mind.id || undefined,
            selfCareId: id,
            title: item.mind.title,
            url: item.mind.url,
            category: mindCategoryId,
            description: item.mind.description,
            file: item.mind.file,
            week: item.week,
          },
        ]
      })
      const files = dataWithFile.flatMap((item) => item.file)
      const resFiles = await uploadMutation.mutateAsync(files)
      const data = {
        form: dataWithFile.map((item, index) => {
          const {file, ...rest} = item
          return resFiles[index]?.id
            ? {
                ...rest,
                image: resFiles[index].id,
              }
            : rest
        }),
      }
      mutation.mutate(data)
    },
    enableReinitialize: true,
  })

  return (
    <div className='card mb-10'>
      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit}>
          <FieldArray name='weeks'>
            {({remove, push}) => (
              <div className='card-body'>
                <div className='d-flex justify-content-between align-items-center py-8'>
                  <div className='ms-6'>
                    <h1 className='fw-bold fs-24 p-0 m-0'>Self Care Plan</h1>
                  </div>
                  <button
                    onClick={() =>
                      push({
                        week:
                          formik.values.weeks.length > 0
                            ? formik.values.weeks[formik.values.weeks.length - 1].week + 1
                            : 1,
                        body: {
                          title: '',
                          // url: '',
                          description: '',
                          category: bodyCategoryId,
                        },
                        mind: {
                          title: '',
                          // url: '',
                          description: '',
                          category: mindCategoryId,
                        },
                      })
                    }
                    type='button'
                    className='btn btn-light-primary'
                  >
                    Add Week
                  </button>
                </div>
                <div className='d-flex flex-column gap-2'>
                  <p className='fs-20'>
                    <span className='fw-bold'>Cycle</span> : {selfCare?.attributes.cycle}
                  </p>
                  <p className='fs-20'>
                    <span className='fw-bold'>Name</span> : {selfCare?.attributes.name}
                  </p>
                  <p className='fs-20'>
                    <span className='fw-bold'>Description</span> :{' '}
                    {selfCare?.attributes.description}
                  </p>
                </div>
                <hr />
                <div className='d-flex flex-column gap-2'>
                  {formik.values.weeks.map((week, index) => (
                    <div key={index} className='d-flex flex-column gap-2'>
                      <div className='d-flex justify-content-between align-items-center py-8'>
                        <div className='ms-6'>
                          <h1 className='fw-bold fs-24 p-0 m-0'>Week {index + 1}</h1>
                        </div>
                        <button
                          onClick={() => {
                            ;[week.body?.id, week.mind?.id].forEach(
                              (id) => id && deleteMutation.mutate(id)
                            )
                            remove(index)
                          }}
                          type='button'
                          className='btn btn-light-danger'
                        >
                          Remove Week
                        </button>
                      </div>
                      <div className='d-flex flex-column gap-4'>
                        <div className='row'>
                          <h4 className='col-5 text-center fs-24'>Activity</h4>
                          {/* <h4 className='col-5 text-center fs-24'>Article</h4> */}
                          {/* <h4 className='col-2 text-end fs-24 pe-9'>Image</h4> */}
                        </div>
                        <div className='d-flex justify-content-between align-items-center gap-4'>
                          <div className='d-flex justify-content-center align-items-center'>
                            <h4 className='fs-24'>Body</h4>
                          </div>
                          <InputDefault
                            type='text'
                            placeholder='Title'
                            id={`weeks.${index}.body.title`}
                            name={`weeks.${index}.body.title`}
                            value={week.body.title}
                            onChange={formik.handleChange}
                          />
                          {/* <InputDefault
                            type='text'
                            placeholder='Article'
                            id={`weeks.${index}.body.url`}
                            name={`weeks.${index}.body.url`}
                            value={week.body.url}
                            onChange={formik.handleChange}
                          /> */}
                          <button
                            onClick={() =>
                              setSelectedDescription({
                                name: `weeks.${index}.body.description`,
                                value: week.body.description,
                              })
                            }
                            type='button'
                            className='btn btn-light-warning'
                            data-bs-toggle='modal'
                            data-bs-target='#modal-rich-editor'
                          >
                            Description
                          </button>
                          <label
                            htmlFor={`upload-input-body-${index}`}
                            className='col-2 text-truncate btn btn-light-primary'
                          >
                            {(formik.values.weeks[index].body.file as File)?.name ||
                              week.body?.imageUrl ||
                              'Upload'}
                          </label>
                          <input
                            type='file'
                            id={`upload-input-body-${index}`}
                            className='d-none'
                            onChange={(e) => {
                              formik.setFieldValue(`weeks.${index}.body.file`, e.target.files![0])
                            }}
                          />
                        </div>
                        <div className='d-flex justify-content-between align-items-center gap-4'>
                          <div className='d-flex justify-content-center align-items-center'>
                            <h4 className='fs-24'>Mind</h4>
                          </div>
                          <InputDefault
                            type='text'
                            placeholder='Title'
                            id={`weeks.${index}.mind.title`}
                            name={`weeks.${index}.mind.title`}
                            value={week.mind.title}
                            onChange={formik.handleChange}
                          />
                          {/* <InputDefault
                            type='text'
                            placeholder='Article'
                            id={`weeks.${index}.mind.url`}
                            name={`weeks.${index}.mind.url`}
                            value={week.mind.url}
                            onChange={formik.handleChange}
                          /> */}
                          <button
                            onClick={() =>
                              setSelectedDescription({
                                name: `weeks.${index}.mind.description`,
                                value: week.mind.description,
                              })
                            }
                            type='button'
                            className='btn btn-light-warning'
                            data-bs-toggle='modal'
                            data-bs-target='#modal-rich-editor'
                          >
                            Description
                          </button>
                          <label
                            htmlFor={`upload-input-mind-${index}`}
                            className='col-2 text-truncate btn btn-light-primary'
                          >
                            {(formik.values.weeks[index].mind.file as File)?.name ||
                              week.mind?.imageUrl ||
                              'Upload'}
                          </label>
                          <input
                            type='file'
                            id={`upload-input-mind-${index}`}
                            className='d-none'
                            onChange={(e) => {
                              formik.setFieldValue(`weeks.${index}.mind.file`, e.target.files![0])
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </FieldArray>
          <div className='d-flex justify-content-between p-8 gap-4'>
            <div className='btn-group' role='group' aria-label='Prev / Next'>
              <button
                type='button'
                disabled={!prevCycle?.[0]?.id}
                className='btn btn-secondary'
                onClick={() => navigate(`/self-care-plan/self-caredetail/${prevCycle?.[0]?.id}`)}
              >
                &lt; Prev Cycle
              </button>
              <button
                type='button'
                disabled={!nextCycle?.[0]?.id}
                className='btn btn-secondary'
                onClick={() => navigate(`/self-care-plan/self-caredetail/${nextCycle?.[0]?.id}`)}
              >
                Next Cycle &gt;
              </button>
            </div>
            <button type='submit' className='btn btn-primary'>
              Submit
            </button>
          </div>
        </form>
      </FormikProvider>
      <ModalRichEditor
        descriptionState={[selectedDescription, setSelectedDescription]}
        setValue={formik.setFieldValue}
      />
    </div>
  )
}

export default SelfCareDetailWrapper
